import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { finalize, map, mergeMap, startWith, switchMap } from 'rxjs/operators';

import { ActiveIngredientsSettingState } from '../../activeingredientsetting.model';
import { ActiveIngredientSettingsDialogComponent } from '../../components/activeingredientsetting-dialog/activeingredientsetting-dialog.component';

import { ActiveIngredientSettingsService } from '../../services/activeingredientsetting.service';
import * as ActiveIngredientSettingsActions from '../actions/activeingredientsetting.actions';

@Injectable()
export class ActiveIngredientSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(ActiveIngredientSettingsService);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly store = inject<Store<ActiveIngredientsSettingState>>(Store);

  loadActiveIngredientSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveIngredientSettingsActions.loadActiveIngredientSettingsRequest),
      mergeMap(() =>
        this.api.getActiveIngredientSettings().pipe(
          map((activeIngredientSettings) =>
            ActiveIngredientSettingsActions.loadActiveIngredientSettingsSuccess({
              activeIngredientSettings,
            }),
          ),
        ),
      ),
    ),
  );

  addActiveIngredientSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveIngredientSettingsActions.addActiveIngredientSetting),
      map((_) =>
        ActiveIngredientSettingsActions.editActiveIngredientSetting({
          activeIngredientSetting: {
            id: 0,
            ingredientId: null,
            ingredientName: '',
            calendarYear: null,
            malusPoints: null,
            includeInSum: null,
            forbidden: null,
          },
        }),
      ),
    ),
  );

  editActiveIngredientSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveIngredientSettingsActions.editActiveIngredientSetting),
      mergeMap((action) =>
        this.dialog
          .form(ActiveIngredientSettingsDialogComponent, {
            activeIngredientSetting: {
              ...action.activeIngredientSetting,
            },
            title: action.activeIngredientSetting.ingredientId
              ? this.locale.translate('editActiveIngredientSetting')
              : this.locale.translate('addActiveIngredientSetting'),
          })
          .pipe(
            switchMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? of(
                    ActiveIngredientSettingsActions.upsertActiveIngredientSettings({
                      activeIngredientSetting: { id: action.activeIngredientSetting.id, ...result },
                    }),
                  )
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  upsertActiveIngredientSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveIngredientSettingsActions.upsertActiveIngredientSettings),
      mergeMap((action) =>
        this.api.upsertActiveIngredientSettings(action.activeIngredientSetting).pipe(
          switchMap(() => [ActiveIngredientSettingsActions.loadActiveIngredientSettingsRequest()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteActiveIngredientSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveIngredientSettingsActions.deleteActiveIngredientSetting),
      mergeMap((action) =>
        this.api.deleteActiveIngredientSetting(action.activeIngredientSetting).pipe(
          switchMap(() => [ActiveIngredientSettingsActions.loadActiveIngredientSettingsRequest()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
